import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import useApi from 'hooks/useApi';
import {
  getDocumentDetails,
  getReadDocument,
  getDocumentValue,
  setNavigationPane,
  refreshPath,
  getPathName,
  goBackClientDocument,
  getDocumentPath,
} from 'redux/actions/documentActions';
import { loadDocument } from 'redux/actions/resultActions';
import { addBreadcrumb, removeBreadcrumb } from 'redux/actions/layoutActions';

import { toBase64 } from 'shared/utils';
import DocumentForm from 'shared/components/DocumentForm';
import { LabelIcon } from 'shared/components/Labels';
import { colors } from 'shared/defaultTheme';
import { ReactComponent as DocumentIcon } from 'assets/icon-document.svg';
import { ReactComponent as FolderIcon } from 'assets/icon-folder.svg';
import { DocumentationBackground } from '../styles';

const PATH_ID = '1/';

const WordDocumentForm = () => {
  const { customAreaApi } = useApi();
  const dispatch = useDispatch();
  const history = useHistory();
  const theme = useSelector((st) => st.theme);
  const { currentClient } = useSelector((state) => state.clientState);
  const { pathname } = useLocation();
  const { mainColor } = theme;
  const { cusNavId, mail, name, surname, isCustomer, clientId } = useSelector((st) => st.auth);
  const { selectedDefaultLanguage } = useSelector((sta) => sta.topBar);
  const documentState = useSelector((state) => state.document);
  const { breadcrumbs } = useSelector((state) => state.layout);
  const [client, setClient] = useState('');
  const [newCurrentClient, setNewCurrentClient] = useState(null);
  const { t } = useTranslation();
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [uploadedError, setUploadedError] = useState('');
  const [disbleDocument, setDisbleDocument] = useState(false);
  const [openContactsModal, setOpenContactsModal] = useState(false);
  const [states, setStates] = useState([]);
  const [tags, setTags] = useState([]);
  const [contacts, setContacts] = useState();
  const [color, setColor] = useState(null);
  const [disableButton, setDisabledButton] = useState(false);
  const [currentColor, setCurrentColor] = useState(null);
  const newDocs = useRef(null);
  const newState = useRef(null);
  const newTags = useRef(null);
  const newPublic = useRef(null);
  const newPermissionContacts = useRef(null);

  useEffect(() => {
    const documentFormState = localStorage.getItem('documentFormState');
    const localizacionPath = localStorage.getItem('localizacionPath');
    const setPath = localStorage.getItem('setPath');
    if (documentFormState && localizacionPath && setPath) {
      dispatch(getDocumentDetails(JSON.parse(documentFormState)));
      dispatch(getPathName(localizacionPath));
      dispatch(getDocumentPath(setPath));
    }
  }, []);

  const [, getStatesData] = customAreaApi(
    {
      url: `${window.API_V1}/cus/states/${cusNavId}`,
      method: 'GET',
    },
    { manual: true }
  );

  const [, getTagsData] = customAreaApi(
    {
      url: `${window.API_V1}/cus/tags/${cusNavId}`,
      method: 'GET',
    },
    { manual: true }
  );

  const [, getContactsData] = customAreaApi(
    {
      method: 'GET',
    },
    { manual: true }
  );

  const [{ loading: loadingCreate }, createDocument] = customAreaApi(
    {
      method: 'POST',
    },
    { manual: true }
  );

  const [{ loading: loadingUpdate }, updateDocument] = customAreaApi(
    {
      method: 'POST',
    },
    { manual: true }
  );

  const [{ loading: loadingPublicDocument }, updatePublicDocument] = customAreaApi(
    {
      method: 'POST',
    },
    { manual: true }
  );

  const [, getClientsData] = customAreaApi(
    {
      url: `${window.API_V1}/cli/clients/${cusNavId}/search`,
      method: 'POST',
    },
    { manual: true }
  );
  useEffect(() => {
    if (breadcrumbs.length > 1) {
      breadcrumbs.forEach(() => {
        dispatch(removeBreadcrumb());
      });
      dispatch(
        addBreadcrumb({
          active: false,
          label: 'general_home',
          link: '/home',
          isModify: false,
        })
      );
      dispatch(
        addBreadcrumb({
          label: 'general_pagination_documents',
          active: true,
          link: 'documentations-client',
          isModify: true,
        })
      );
    }

    if (documentState.documentDtails && documentState.documentDtails.isFile) {
      dispatch(
        addBreadcrumb({
          label: client,
          active: true,
          link: '/documentations',
          isModify: false,
          client: newCurrentClient,
        })
      );
      dispatch(
        addBreadcrumb({
          label: 'new_document_word_edit_title',
          active: true,
          link: pathname,
          isModify: true,
        })
      );
    } else if (documentState.documentDtails && !documentState.documentDtails.isFile) {
      dispatch(
        addBreadcrumb({
          label: client,
          active: true,
          link: '/documentations',
          isModify: false,
          client: newCurrentClient,
        })
      );
      dispatch(
        addBreadcrumb({
          label: 'new_folder_word_edit_title',
          active: true,
          link: pathname,
          isModify: true,
        })
      );
    } else {
      dispatch(
        addBreadcrumb({
          label: currentClient?.clientName,
          active: true,
          link: '/documentations',
          isModify: false,
        })
      );
      dispatch(
        addBreadcrumb({
          label: 'documents_new_word',
          active: true,
          link: pathname,
          isModify: true,
        })
      );
    }
  }, [documentState.documentDtails, currentClient, client, newCurrentClient]);

  const clientsData = async () => {
    const {
      data: { data },
    } = await getClientsData({
      data: {
        filters: [
          {
            model: 'Client',
            field: 'ClientId',
            filter: [documentState.documentDtails.clientId],
          },
        ],
        reduce: {
          limit: 0,
          skip: 0,
        },
      },
    });
    setClient(data[0].clientName);
    setNewCurrentClient(data[0]);
  };

  useEffect(() => {
    if (documentState.documentDtails) {
      clientsData();
      setDisbleDocument(!!documentState.documentDtails);
    }
  }, [documentState.documentDtails]);

  useEffect(() => {
    const getColor = (value) => {
      const findColor = states.find((x) => x.id === value);
      setColor(findColor?.color);
    };
    if (newState.current && states.length > 0) {
      getColor(newState.current);
    }
  }, [newState.current, states.length > 0, currentColor]);

  useEffect(() => {
    const getStateList = async () => {
      const {
        data: { data },
      } = await getStatesData();
      setStates(data);
    };
    const getTagList = async () => {
      const {
        data: { data },
      } = await getTagsData();
      setTags(data);
    };
    const fetchContacts = async () => {
      try {
        const {
          data: { data },
        } = await getContactsData({
          url: `${window.API_V1}/cli/contacts/${cusNavId}/${
            documentState.readDocument?.clientId || documentState.documentDtails?.clientId
          }`,
        });
        const filterContacts = data.filter((contact) => contact.mail);
        setContacts(filterContacts);
      } catch (error) {
        console.error(`Error Contacts: ${error}`);
      }
    };
    fetchContacts();
    getTagList();
    getStateList();
  }, [selectedDefaultLanguage, documentState.documentDtails]);

  useEffect(() => {
    return () => {
      dispatch(setNavigationPane(false));
      // dispatch(getReadDocument(null));
      dispatch(getDocumentDetails(null));
      dispatch(getPathName(''));
    };
  }, []);

  const onErrorLogoUploading = (error) => {
    setUploadedError(error);
  };

  const onToggleError = () => setShowError((currentShow) => !currentShow);

  const onUpdateDocument = () => {
    setDisbleDocument(!disbleDocument);
  };

  const onEditDocument = async (values) => {
    try {
      setDisabledButton(true);
      const { id, clientNavisionId, pathId, alerts, type, sharepoint, createdBy, isFile } =
        documentState.documentDtails;
      const extensionExists = values.name.lastIndexOf('.');
      const currentExtension = documentState.documentDtails.name.substring(
        documentState.documentDtails.name.lastIndexOf('.')
      );
      if (
        (isFile && extensionExists !== -1 && values.name.endsWith(currentExtension)) ||
        (!isFile && values.name !== '')
      ) {
        const curretFileBase64 =
          values.docs !== '' && isFile
            ? await toBase64(values.docs)
            : documentState.documentDtails?.data;
        let sendTo = [];
        if (!!values.public && values.permissionContacts && values.permissionContacts.length > 0) {
          values.permissionContacts.forEach((contactMail) => {
            const mailExist = sendTo.some((m) => m.u === mail);
            if (!mailExist) {
              sendTo = [...sendTo, { u: contactMail, p: 'r--' }];
            }
          });
        }
        const newData = {
          id,
          ...(isFile
            ? {
                name: values.docs !== '' ? values.docs.name : values.name,
                type: values.docs !== '' ? values.docs.type : type,
                size: values.docs !== '' ? values.docs.size : values.size,
              }
            : {
                name: values.name,
                type,
                size: values.size,
              }),
          description: values.description,
          pathId,
          tags: values.tags,
          state: values.state,
          sharepoint,
          version: values.version,
          alerts,
          permissions: sendTo,
          data: curretFileBase64,
          parentPathId: documentState.path,
          readers: [],
          createdBy,
        };
        await updateDocument({
          data: newData,
          url: `${window.API_V1}/cli/items/${clientNavisionId}/${
            documentState.documentDtails.clientId
          }/${encodeURIComponent(mail)}/modify`,
        });
        dispatch(getDocumentDetails(null));
        dispatch(getReadDocument(null));
        dispatch(getDocumentValue(null));
        dispatch(loadDocument(true));
        dispatch(refreshPath(true));
        dispatch(setNavigationPane(true));
        localStorage.removeItem('documentFormState');
        if (documentState.isClientDocument && documentState.documentDtails?.clientId) {
          dispatch(goBackClientDocument(true));
          history.push(`/clients/update/${documentState.documentDtails.clientId}`);
        } else {
          history.push('/documentations');
        }
      } else if (isFile) {
        setShowError(true);
        setErrorMessage(t('documents_content_rename_confirmation'));
        setDisabledButton(false);
      } else {
        setShowError(true);
        setErrorMessage(t('folders_content_rename_confirmation'));
        setDisabledButton(false);
      }
    } catch (error) {
      setShowError(true);
      if (error.response?.data.errors[0].code === 'CCIT-031') {
        setErrorMessage(t('document_permission_edit', { value: mail }));
      } else if (error.response?.data.errors[0].code === 'CCIT-16') {
        setErrorMessage(t('general_error_notification'));
      } else {
        setErrorMessage(t('custom_field_operation_error_message'));
      }
      setDisabledButton(false);
    }
  };

  const onUpdatePublicDocument = async (values) => {
    try {
      await updatePublicDocument({
        data: values,
        url: `${window.API_V1}/cli/items/${cusNavId}/${clientId}/${encodeURIComponent(
          mail
        )}/publishAdmin`,
      });
      history.push('/documentations');
    } catch (error) {
      setShowError(true);
      setErrorMessage(t('custom_field_operation_error_message'));
    }
  };

  const onCreateDocument = async (values) => {
    let sendTo = [];
    let sendToAdmin = [];
    if (
      isCustomer &&
      !!values.public &&
      values.permissionContacts &&
      values.permissionContacts.length > 0
    ) {
      values.permissionContacts.forEach((contactMail) => {
        sendTo = [...sendTo, { u: contactMail, p: 'r--' }];
      });
    }
    if (!isCustomer && !!values.public) {
      values.permissionContacts.forEach((contactMail) => {
        sendToAdmin = [...sendToAdmin, { u: contactMail, p: 'rwx' }];
      });
    }
    const curretFileBase64 = await toBase64(values.docs);
    try {
      setDisabledButton(true);
      const newData = [
        {
          name: values.docs.name,
          description: values.description,
          tags: values.tags,
          state: values.state,
          size: values.docs.size,
          version: values.version,
          permissions: sendTo,
          data: curretFileBase64,
          parentPathId: documentState.path,
          pathId: documentState.readDocument?.pathId || PATH_ID,
          createdBy: { nameComplete: `${name} ${surname}`, mail },
        },
      ];
      const {
        data: { data },
      } = await createDocument({
        data: newData,
        url: `${window.API_V1}/cli/items/${documentState.readDocument?.clientNavisionId}/${
          documentState.readDocument?.clientId
        }/${encodeURIComponent(mail)}?publish=${sendTo.length > 0}`,
      });
      dispatch(getReadDocument(null));
      dispatch(removeBreadcrumb());
      dispatch(refreshPath(true));
      dispatch(setNavigationPane(true));
      localStorage.removeItem('documentFormState');
      if (documentState.isClientDocument && documentState.readDocument?.clientId) {
        dispatch(goBackClientDocument(true));
        history.push(`/clients/update/${documentState.readDocument.clientId}`);
      } else if (!isCustomer) {
        const newDataPublish = [{ ...data[0], permissions: sendToAdmin }];
        onUpdatePublicDocument(newDataPublish);
      } else {
        history.push('/documentations');
      }
    } catch (error) {
      setShowError(true);
      if (error.response?.data.errors[0].code === 'CCIT-16') {
        setErrorMessage(t('general_error_notification'));
      } else {
        setErrorMessage(t('custom_field_operation_error_message'));
      }
      setDisabledButton(false);
    }
  };

  const onSubmit = async (values) => {
    if (documentState.documentDtails) {
      onEditDocument(values);
    } else {
      onCreateDocument(values);
    }
  };

  const onCancel = () => {
    dispatch(loadDocument(true));
    dispatch(getDocumentDetails(null));
    dispatch(getReadDocument(null));
    dispatch(refreshPath(true));
    dispatch(setNavigationPane(true));
    dispatch(goBackClientDocument(true));
    if (documentState.isClientDocument && documentState.documentDtails?.clientId) {
      history.push(`/clients/update/${documentState.documentDtails.clientId}`);
    } else {
      history.goBack();
    }
  };
  return (
    <div className="pb-3">
      {tags.length > 0 && states.length > 0 ? (
        <DocumentationBackground className="mr-2">
          <LabelIcon
            label={t(
              // eslint-disable-next-line no-nested-ternary
              documentState && documentState.documentDtails && documentState.documentDtails.isFile
                ? 'document_detail_title'
                : documentState && documentState.documentDtails
                ? 'folder_detail_title'
                : 'new_document_word_title'
            )}
            Icon={() =>
              documentState &&
              documentState.documentDtails &&
              !documentState.documentDtails.isFile ? (
                <FolderIcon className="label-icon__folder" fill={colors.white} />
              ) : (
                <DocumentIcon className="label-icon__document" fill={colors.white} />
              )
            }
            color={mainColor}
            className="ml-1 label-icon text-uppercase"
            classNameMain="mb-5"
          />
          {contacts && (
            <DocumentForm
              client={client}
              showError={showError}
              errorMessage={errorMessage}
              uploadedError={uploadedError}
              color={color}
              onErrorLogoUploading={onErrorLogoUploading}
              onToggleError={onToggleError}
              onUpdateDocument={onUpdateDocument}
              onSubmit={onSubmit}
              onCancel={onCancel}
              disbleDocument={disbleDocument}
              states={states}
              tags={tags}
              newDocs={newDocs}
              newState={newState}
              newTags={newTags}
              newPublic={newPublic}
              newPermissionContacts={newPermissionContacts}
              setOpenContactsModal={setOpenContactsModal}
              openContactsModal={openContactsModal}
              loadingCreate={loadingCreate}
              loadingUpdate={loadingUpdate}
              loadingPublicDocument={loadingPublicDocument}
              setCurrentColor={setCurrentColor}
              contacts={contacts}
              disableButton={disableButton}
              setErrorMessage={setErrorMessage}
            />
          )}
        </DocumentationBackground>
      ) : null}
    </div>
  );
};

export default WordDocumentForm;
